import { TEvent } from '@ws/shared/types';

import { TShortNoteDict } from '../../resources/syncService';
import { THealthKind, THealthSource } from '../../utils/EventTrain/types';
import { TSyncSessionToken } from '../../utils/SyncGroup/session.types';

export enum SyncStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  CONFLICT = 'CONFLICT',
  PAUSE = 'PAUSE',
}

// Причина текущего статуса (для PAUSE): управляет tooltip'ом индикатора (§2.8).
export type TSyncStatusReason =
  | 'offline'
  | 'hidden'
  | 'auth-required'
  | 'transport'
  | 'sync-error'
  | 'eligibility-error'
  | 'local-storage-error'
  | null;

export interface ISyncState {
  sync: {
    // Токен сессии, к которой относится текущий статус (projectId + generation).
    // Reducer применяет SYNC_STATE_COMMIT только при совпадении токена (§2.8).
    session: TSyncSessionToken | null;
    // true с открытия проекта до первого коммита reconcile — UI показывает нейтрально.
    hydrating: boolean;
    // null = ещё неизвестно (до гидратации).
    isEnabled: boolean | null;
    status: SyncStatus;
    reason: TSyncStatusReason;
  };
  conflictState: {
    dispenser: TDispenser;
    registry: TEventRegistry;
    conflicts: TConflictDict;
    remoteEvents: TEvent[];
    localEvents: TEvent[];
    applyOnClient: TEvent[];
    applyOnServer: TEvent[];
  };
}

export type TEventId = string;
export type TEventType = string;
export type TConflictId = string;
export type TSourceIdxMark = string;
export type TResolveDirection = 'remote' | 'local' | 'no_moves';

export type TSyncState = {
  processedRemotesIdx: 0;
  processedLocalsIdx: 0;
};

/*
    key: entityId
        key: eventType
            conflictId: number | null
            isUpdated: bool
            remotes: [event idx]
            locals: [event idx]
 */
export type TEventRegistry = {
  [key: TEventId]: {
    [key: TEventType]: {
      conflictId: TConflictId | null;
      isUpdated: boolean;
      idxsOfRemotes: number[];
      idxsOfLocals: number[];
    };
  };
};

/*
    key: {source}_{idx}
        conflictId
 */
export type TDispenser = {
  [key: TSourceIdxMark]: TConflictId;
};

/*
    key: conflictId
        eventType
        remotes [events]
        locals [events]
        resolvedTo: remote | local | null
 */
export type TConflict = {
  id: TConflictId;
  eventType: TEventType;
  remoteEvents: TEvent[];
  localEvents: TEvent[];
  resolvedTo: TResolveDirection | null;
};

export type TConflictDict = {
  [key: TConflictId]: TConflict;
};

// Открытие сессии проекта: сбрасывает слайс и адоптирует токен (несёт generation,
// которого reducer иначе не знает). Диспатчит saga сразу после beginSession (§2.8).
export interface ISyncStateResetAction {
  session: TSyncSessionToken;
}
export const SYNC_STATE_RESET = 'SYNC_STATE_RESET';

// Единственный мутатор статуса (control-plane). Reducer применяет ТОЛЬКО при совпадении
// session с текущим (projectId И generation) и снимает hydrating (§2.8).
export interface ISyncStateCommitAction {
  session: TSyncSessionToken;
  isEnabled?: boolean;
  status: SyncStatus;
  reason?: TSyncStatusReason;
}
export const SYNC_STATE_COMMIT = 'SYNC_STATE_COMMIT';

// --- Control-plane команды/триггеры супервизора (§2.7) ---
export type TReconcileReason =
  | 'bootstrap'
  | 'project-set'
  | 'auth-change'
  | 'network'
  | 'visibility'
  | 'command'
  | 'health'
  | 'logout';
export interface ISyncReconcileAction {
  reason?: TReconcileReason;
}
export const SYNC_RECONCILE = 'SYNC_RECONCILE';

// Сериализованные мутирующие команды (замена INIT_SYNC/DESTROY_SYNC).
export const SYNC_CMD_ENABLE = 'SYNC_CMD_ENABLE';
export const SYNC_CMD_DISABLE = 'SYNC_CMD_DISABLE';

// Health-мост от EventTrain (§2.5).
export interface ISyncHealthAction {
  session?: TSyncSessionToken;
  // Переиспользуем типы EventTrain — единый источник правды, иначе новый kind рассинхронит
  // (terminal-failure добавлялся именно так: тип действия отставал от THealthKind).
  kind: THealthKind;
  source: THealthSource;
}
export const SYNC_HEALTH = 'SYNC_HEALTH';

// Намерение выйти (отдельно от USER_SET_AUTH_STATUS(false), который приходит и от 401).
export const LOGOUT_INTENT = 'LOGOUT_INTENT';

export const SYNC_STATE_ASK_DELTA = 'SYNC_STATE_ASK_DELTA';

export interface IOnDeltaReceivedAction {
  // Токен сессии, породившей delta (train проставляет его при эмите). deltaWorker
  // отсекает delta чужой/устаревшей сессии. Опционален для обратной совместимости.
  session?: TSyncSessionToken;
  remote: TEvent[];
  local: TEvent[];
  notes: TShortNoteDict;
}

export const ON_DELTA_RECEIVED = 'ON_DELTA_RECEIVED';

export interface IUpdateConflictStateAction {
  dispenser?: TDispenser;
  registry?: TEventRegistry;
  conflicts?: TConflictDict;
  remoteEvents?: TEvent[];
  localEvents?: TEvent[];
  applyOnClient?: TEvent[];
  applyOnServer?: TEvent[];
}

export const UPDATE_CONFLICT_STATE = 'UPDATE_CONFLICT_STATE';
